import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, tap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { ApiUrlService } from '../services/api-url.service';
import { MsalService } from '@azure/msal-angular';

const PERMISSION_TOKEN_HEADER = 'x-permission-token';
const PERMISSION_TOKEN_STORAGE_KEY = 'brs_permission_token';

export const brsInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const snackbarService = inject(SnackbarV3Service);
  const msalService = inject(MsalService);
  const apiBaseUrl = inject(ApiUrlService).baseUrl;

  if (!req.url.startsWith(apiBaseUrl)) {
    return next(req);
  }

  const storedToken = sessionStorage.getItem(PERMISSION_TOKEN_STORAGE_KEY);
  const outgoingReq = storedToken ? req.clone({ setHeaders: { [PERMISSION_TOKEN_HEADER]: storedToken } }) : req;

  return next(outgoingReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const newToken = event.headers.get(PERMISSION_TOKEN_HEADER);
        if (newToken) {
          const activeNonce = msalService.instance.getActiveAccount()?.idTokenClaims?.nonce;
          const brsPayload = decodeJwtPayload(newToken);
          const jtiGea = brsPayload?.['jti_gea'] as string | undefined;

          if (activeNonce && jtiGea && activeNonce === jtiGea) {
            sessionStorage.setItem(PERMISSION_TOKEN_STORAGE_KEY, newToken);
          }
        }
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (!isSuppressedError(req, err)) {
        snackbarService.add({
          summary: 'Error',
          detail: getErrorDetails(err),
          severity: 'error',
        });
        console.error('Error occurred:', err);
      }
      return throwError(() => err);
    })
  );
};

const suppressedErrors: { urlPattern: RegExp; status: number }[] = [
  { urlPattern: /\/projects\/[^/]+\/reportset$/, status: 404 },
  { urlPattern: /\/projects\/[^/]+$/, status: 409 },
  { urlPattern: /\/projects\/[^/]+\/products\/[^/]+$/, status: 409 },
  { urlPattern: /\/products\/[^/]+\/calculation-status$/, status: 404 },
  { urlPattern: /\/products\/[^/]+\/outputs\/select$/, status: 409 },
];

const isSuppressedError = (req: HttpRequest<unknown>, err: HttpErrorResponse): boolean =>
  suppressedErrors.some((rule) => rule.status === err.status && rule.urlPattern.test(req.url));

const getErrorDetails = (err: HttpErrorResponse) => {
  // Add context specific error messages until a generic error modal is agreed on.
  const errors = err.error?.errors;
  if (errors) {
    const firstKey = Object.keys(errors)[0];
    if (firstKey && errors[firstKey]?.[0]) {
      return errors[firstKey][0];
    }
  }
  return err.error?.message ?? err.message;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};
